import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  FileText,
  Plus,
  Server,
  Wrench,
  Clock,
  Euro,
  Calendar,
  Globe,
  MoreHorizontal,
  CheckCircle2,
  AlertCircle,
  Play,
  Pause,
  Search,
  Loader2,
  Building2,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ExternalLink,
  UserPlus,
  Users,
  FileSignature,
  PenLine
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const ContractsPage = () => {
  const { user, isAgency, isClient } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState([]);
  
  // Sorting state
  const [sortField, setSortField] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Filter state
  const [filterType, setFilterType] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterSignature, setFilterSignature] = useState('all');

  const [stats, setStats] = useState({
    total_active: 0,
    hosting_count: 0,
    maintenance_count: 0,
    monthly_revenue: 0,
    pending_signature: 0
  });

  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [thirdPartyServices, setThirdPartyServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  const [newContract, setNewContract] = useState({
    name: '',
    client_id: '',
    contract_type: 'both',
    monthly_price: '',
    hours_included: '',
    start_date: new Date().toISOString().split('T')[0],
    engagement: '12 mois',
    auto_renew: true,
    website_url: ''
  });

  const [newTask, setNewTask] = useState({
    contract_id: '',
    title: '',
    description: '',
    task_type: 'initiative',
    scheduled_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsRes, tasksRes] = await Promise.all([
        api.get('/contracts/'),
        api.get('/contracts/tasks/')
      ]);
      setContracts(contractsRes.data);
      setTasks(tasksRes.data);

      // If agency, also fetch stats, clients, team members, and third-party services
      if (isAgency) {
        const [statsRes, clientsRes, teamRes, servicesRes] = await Promise.all([
          api.get('/contracts/stats'),
          api.get('/users/?role=client'),
          api.get('/users/?role=team_member'),
          api.get('/settings/third-party-services').catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setClients(clientsRes.data);
        setTeamMembers(teamRes.data);
        setThirdPartyServices(servicesRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Handle service selection to prefill form
  const handleServiceSelect = (serviceId) => {
    if (!serviceId) {
      setSelectedService(null);
      setNewContract(prev => ({
        ...prev,
        name: '',
        monthly_price: '',
        hours_included: '',
        contract_type: 'both',
        service_id: null,
        service_content: null
      }));
      return;
    }
    
    const service = thirdPartyServices.find(s => s.id === serviceId);
    if (service) {
      setSelectedService(service);
      // Determine contract type from service
      let contractType = 'services';
      if (service.name?.toLowerCase().includes('hébergement')) {
        contractType = 'hosting';
      } else if (service.name?.toLowerCase().includes('maintenance')) {
        contractType = 'maintenance';
      }
      
      // Use sale_price as the monthly price (this is the price shown to clients)
      const monthlyPrice = service.sale_price || service.price_monthly || service.price || '';
      
      setNewContract(prev => ({
        ...prev,
        name: service.name || '',
        monthly_price: monthlyPrice,
        hours_included: service.hours_included || '',
        contract_type: contractType,
        service_id: service.id,
        service_content: service.description || service.content || null
      }));
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...newContract,
        monthly_price: parseFloat(newContract.monthly_price) || 0,
        hours_included: parseFloat(newContract.hours_included) || 0,
        // Include service reference if selected from Services Tiers
        service_id: selectedService?.id || null,
        service_content: selectedService?.description || selectedService?.content || null
      };
      await api.post('/contracts/', payload);
      toast.success('Contrat client créé avec succès !');
      setShowCreateModal(false);
      setSelectedService(null);
      setNewContract({
        name: '',
        client_id: '',
        contract_type: 'both',
        monthly_price: '',
        hours_included: '',
        start_date: new Date().toISOString().split('T')[0],
        engagement: '12 mois',
        auto_renew: true,
        website_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/contracts/tasks', newTask);
      toast.success('Tâche créée avec succès !');
      setShowTaskModal(false);
      setNewTask({ contract_id: '', title: '', description: '', task_type: 'initiative', scheduled_date: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const startTask = async (taskId) => {
    try {
      await api.post(`/contracts/tasks/${taskId}/start`);
      toast.success('Chronomètre démarré !');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const completeTask = async (taskId, duration) => {
    try {
      await api.post(`/contracts/tasks/${taskId}/complete`, {
        duration_minutes: parseInt(duration) || 0
      });
      toast.success('Tâche terminée !');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateContractStatus = async (contractId, status) => {
    try {
      await api.put(`/contracts/${contractId}/status`, { status });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAssignTeamToContract = async () => {
    if (!selectedContract || selectedTeamMemberIds.length === 0) {
      toast.error('Veuillez sélectionner au moins un membre');
      return;
    }
    setCreating(true);
    try {
      await api.put(`/contracts/${selectedContract.id}/assign-team`, { 
        team_member_ids: selectedTeamMemberIds 
      });
      toast.success('Équipe assignée au contrat !');
      setShowAssignTeamModal(false);
      setSelectedTeamMemberIds([]);
      setSelectedContract(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setCreating(false);
    }
  };

  const openAssignTeamModal = (contract) => {
    setSelectedContract(contract);
    setSelectedTeamMemberIds(contract.assigned_team || []);
    setShowAssignTeamModal(true);
  };

  const toggleTeamMemberSelection = (memberId) => {
    setSelectedTeamMemberIds(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const filteredContracts = useMemo(() => {
    let result = contracts.filter(c =>
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Filter by type
    if (filterType !== 'all') {
      result = result.filter(c => c.contract_type === filterType);
    }
    
    // Filter by client
    if (filterClient !== 'all') {
      result = result.filter(c => c.client_id === filterClient);
    }
    
    // Filter by signature status
    if (filterSignature !== 'all') {
      if (filterSignature === 'pending') {
        result = result.filter(c => c.status === 'pending_signature' || !c.client_signature);
      } else if (filterSignature === 'signed') {
        result = result.filter(c => c.client_signature && c.status !== 'pending_signature');
      }
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'client_name':
          aVal = a.client_name || '';
          bVal = b.client_name || '';
          break;
        case 'contract_type':
          aVal = a.contract_type || '';
          bVal = b.contract_type || '';
          break;
        case 'updated_at':
        case 'last_intervention':
          aVal = new Date(a.updated_at || a.created_at || 0);
          bVal = new Date(b.updated_at || b.created_at || 0);
          break;
        case 'start_date':
          aVal = new Date(a.start_date || 0);
          bVal = new Date(b.start_date || 0);
          break;
        case 'monthly_price':
          aVal = a.monthly_price || 0;
          bVal = b.monthly_price || 0;
          break;
        case 'name':
        default:
          aVal = a.name || '';
          bVal = b.name || '';
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [contracts, searchQuery, filterType, filterClient, filterSignature, sortField, sortDirection]);
  
  // Calculate pending signatures count
  const pendingSignatureCount = useMemo(() => {
    return contracts.filter(c => c.status === 'pending_signature' || !c.client_signature).length;
  }, [contracts]);
  
  // Get unique clients for filter
  const uniqueClients = useMemo(() => {
    const clientMap = new Map();
    contracts.forEach(c => {
      if (c.client_id && c.client_name) {
        clientMap.set(c.client_id, c.client_name);
      }
    });
    return Array.from(clientMap.entries());
  }, [contracts]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  const getContractTypeBadge = (type) => {
    const styles = {
      hosting: { bg: 'bg-blue-100', color: 'text-blue-700', icon: Server, label: 'Hébergement' },
      maintenance: { bg: 'bg-green-100', color: 'text-green-700', icon: Wrench, label: 'Maintenance' },
      both: { bg: 'bg-purple-100', color: 'text-purple-700', icon: FileText, label: 'Héberg. + Maint.' },
      services: { bg: 'bg-amber-100', color: 'text-amber-700', icon: Clock, label: 'Services mensuels' }
    };
    const style = styles[type] || styles.both;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} ${style.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      expired: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      active: 'Actif',
      pending: 'En attente',
      expired: 'Expiré',
      cancelled: 'Annulé'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              {isClient ? 'Mes contrats' : 'Contrats'}
            </h1>
            <p className="text-[#64748b]">
              {isClient ? 'Hébergement et maintenance de vos sites' : 'Gestion des contrats d\'hébergement et maintenance'}
            </p>
          </div>
          {isAgency && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="rounded-full text-white"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau contrat
            </Button>
          )}
        </div>

        {/* Stats - Agency only */}
        {isAgency && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card className="card">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                    <FileText className="w-4 h-4" style={{ color: theme.colors?.accent }} />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Contrats actifs</p>
                    <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.total_active}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
                    <Server className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Hébergement</p>
                    <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.hosting_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100">
                    <Wrench className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Maintenance</p>
                    <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.maintenance_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={cn(
                "card cursor-pointer transition-all hover:shadow-md",
                filterSignature === 'pending' && "ring-2 ring-amber-400"
              )}
              onClick={() => setFilterSignature(filterSignature === 'pending' ? 'all' : 'pending')}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-100">
                    <PenLine className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">À signer</p>
                    <p className="text-lg font-bold" style={{ color: pendingSignatureCount > 0 ? '#d97706' : theme.colors?.primary }}>
                      {pendingSignatureCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : (
          <div className="space-y-6">
              {/* Search & Filters */}
              <Card className="card mb-4">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                      <Input
                        placeholder="Rechercher un contrat..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="input-default pl-10"
                      />
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="px-3 py-2 border rounded-xl text-sm bg-white"
                      >
                        <option value="all">Tous les types</option>
                        <option value="hosting">Hébergement</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="both">Héberg. + Maint.</option>
                      </select>
                      {isAgency && (
                        <select
                          value={filterSignature}
                          onChange={(e) => setFilterSignature(e.target.value)}
                          className="px-3 py-2 border rounded-xl text-sm bg-white"
                        >
                          <option value="all">Toutes signatures</option>
                          <option value="pending">⏳ En attente</option>
                          <option value="signed">✅ Signés</option>
                        </select>
                      )}
                      {isAgency && uniqueClients.length > 0 && (
                        <select
                          value={filterClient}
                          onChange={(e) => setFilterClient(e.target.value)}
                          className="px-3 py-2 border rounded-xl text-sm bg-white"
                        >
                          <option value="all">Tous les clients</option>
                          {uniqueClients.map(([id, name]) => (
                            <option key={id} value={id}>{name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contracts Table */}
              <Card className="card">
                <CardContent className="p-0">
                  {filteredContracts.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
                      <p className="text-[#64748b]">Aucun contrat trouvé</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th 
                              className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                              onClick={() => handleSort('name')}
                            >
                              <div className="flex items-center gap-1">
                                Contrat <SortIcon field="name" />
                              </div>
                            </th>
                            {isAgency && (
                              <th 
                                className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('client_name')}
                              >
                                <div className="flex items-center gap-1">
                                  Client <SortIcon field="client_name" />
                                </div>
                              </th>
                            )}
                            <th 
                              className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                              onClick={() => handleSort('contract_type')}
                            >
                              <div className="flex items-center gap-1">
                                Type <SortIcon field="contract_type" />
                              </div>
                            </th>
                            {isAgency && (
                              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Signature
                              </th>
                            )}
                            <th 
                              className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                              onClick={() => handleSort('updated_at')}
                            >
                              <div className="flex items-center gap-1">
                                Mise à jour <SortIcon field="updated_at" />
                              </div>
                            </th>
                            <th 
                              className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                              onClick={() => handleSort('monthly_price')}
                            >
                              <div className="flex items-center justify-end gap-1">
                                Prix <SortIcon field="monthly_price" />
                              </div>
                            </th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                              Statut
                            </th>
                            {isAgency && (
                              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                Équipe
                              </th>
                            )}
                            <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-10">
                              
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredContracts.map((contract) => (
                            <tr 
                              key={contract.id}
                              className="hover:bg-slate-50 cursor-pointer transition-colors"
                              onClick={() => navigate(`/contracts/${contract.id}`)}
                            >
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{contract.name}</p>
                                  {contract.website_url && (
                                    <a 
                                      href={contract.website_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-xs text-[#64748b] hover:text-blue-600 flex items-center gap-1"
                                    >
                                      <Globe className="w-3 h-3" />
                                      {contract.website_url.replace(/^https?:\/\//, '').substring(0, 25)}
                                      <ExternalLink className="w-2.5 h-2.5" />
                                    </a>
                                  )}
                                </div>
                              </td>
                              {isAgency && (
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                                      <Building2 className="w-3 h-3 text-slate-500" />
                                    </div>
                                    <span className="text-sm text-slate-700">{contract.client_name || '-'}</span>
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-3">
                                {getContractTypeBadge(contract.contract_type)}
                              </td>
                              {isAgency && (
                                <td className="px-4 py-3 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    {contract.client_signature ? (
                                      <div className="flex items-center gap-1 text-emerald-600" title={`Signé le ${contract.client_signature_date ? new Date(contract.client_signature_date).toLocaleDateString('fr-FR') : '-'}`}>
                                        <CheckCircle2 className="w-4 h-4" />
                                        <span className="text-xs font-medium">Client</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 text-amber-500" title="En attente de signature client">
                                        <PenLine className="w-4 h-4" />
                                        <span className="text-xs font-medium">En attente</span>
                                      </div>
                                    )}
                                    {contract.agency_signed && (
                                      <div className="flex items-center gap-1 text-emerald-600" title={`Agence signé le ${contract.agency_signed_at ? new Date(contract.agency_signed_at).toLocaleDateString('fr-FR') : '-'}`}>
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        <span className="text-[10px]">Agence</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-3">
                                <div className="text-sm">
                                  <p className="text-slate-700">
                                    {contract.updated_at 
                                      ? new Date(contract.updated_at).toLocaleDateString('fr-FR')
                                      : contract.start_date?.split('T')[0]
                                    }
                                  </p>
                                  {contract.hours_included > 0 && (
                                    <p className="text-xs text-[#94a3b8]">
                                      {(contract.hours_used || 0).toFixed(1)}h / {contract.hours_included}h
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <p className="font-bold text-sm" style={{ color: theme.colors?.accent }}>
                                  {(contract.monthly_price || 0).toLocaleString('fr-FR')}€
                                </p>
                                <p className="text-xs text-[#64748b]">/mois</p>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {getStatusBadge(contract.status)}
                              </td>
                              {isAgency && (
                                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-1">
                                    {contract.assigned_team?.length > 0 ? (
                                      <div className="flex -space-x-2">
                                        {contract.assigned_team.slice(0, 3).map((memberId, idx) => {
                                          const member = teamMembers.find(m => m.id === memberId);
                                          return (
                                            <div 
                                              key={memberId} 
                                              className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white text-[10px] font-semibold border-2 border-white"
                                              title={member ? `${member.first_name} ${member.last_name}` : ''}
                                            >
                                              {member ? `${member.first_name?.[0]}${member.last_name?.[0]}` : '?'}
                                            </div>
                                          );
                                        })}
                                        {contract.assigned_team.length > 3 && (
                                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-[10px] font-semibold border-2 border-white">
                                            +{contract.assigned_team.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-400">-</span>
                                    )}}
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-6 w-6 ml-1"
                                      onClick={() => openAssignTeamModal(contract)}
                                    >
                                      <UserPlus className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                {isAgency && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                                        <MoreHorizontal className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => {
                                        setNewTask({ ...newTask, contract_id: contract.id });
                                        setShowTaskModal(true);
                                      }}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter une tâche
                                      </DropdownMenuItem>
                                      {contract.status === 'active' && (
                                        <DropdownMenuItem onClick={() => updateContractStatus(contract.id, 'cancelled')}>
                                          <AlertCircle className="w-4 h-4 mr-2 text-red-500" />
                                          Annuler
                                        </DropdownMenuItem>
                                      )}
                                      {contract.status === 'cancelled' && (
                                        <DropdownMenuItem onClick={() => updateContractStatus(contract.id, 'active')}>
                                          <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                                          Réactiver
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Summary */}
              {filteredContracts.length > 0 && (
                <div className="mt-4 text-sm text-slate-500 flex items-center justify-between">
                  <span>{filteredContracts.length} contrat{filteredContracts.length > 1 ? 's' : ''}</span>
                  <span>
                    Total: <strong className="text-slate-700">
                      {filteredContracts.reduce((sum, c) => sum + (c.monthly_price || 0), 0).toLocaleString('fr-FR')}€
                    </strong>/mois
                  </span>
                </div>
              )}
          </div>
        )}

        {/* Create Contract Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) {
            setSelectedService(null);
          }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="create-contract-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouveau contrat client</DialogTitle>
            </DialogHeader>
            <p id="create-contract-desc" className="sr-only">Créer un nouveau contrat client</p>
            <form onSubmit={handleCreateContract} className="space-y-4">
              {/* Service Selection from Services Tiers */}
              {thirdPartyServices.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Créer à partir d'une offre
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Services Tiers</Badge>
                  </Label>
                  <select
                    value={selectedService?.id || ''}
                    onChange={(e) => handleServiceSelect(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl"
                  >
                    <option value="">-- Saisie manuelle --</option>
                    {thirdPartyServices.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name} - {service.sale_price || service.price_monthly || service.price || 0}€/mois
                      </option>
                    ))}
                  </select>
                  {selectedService && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                      <p className="font-medium text-blue-900">{selectedService.name}</p>
                      {selectedService.description && (
                        <p className="text-blue-700 text-xs mt-1 line-clamp-2">{selectedService.description}</p>
                      )}
                      <p className="text-blue-600 mt-1">
                        <span className="font-semibold">{selectedService.sale_price || selectedService.price_monthly || selectedService.price || 0}€</span>/mois
                        {selectedService.hours_included > 0 && ` • ${selectedService.hours_included}h incluses`}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Nom du contrat *</Label>
                <Input
                  value={newContract.name}
                  onChange={(e) => setNewContract({ ...newContract, name: e.target.value })}
                  placeholder="Ex: Hébergement - MonSite.fr"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Client *</Label>
                <select
                  value={newContract.client_id}
                  onChange={(e) => setNewContract({ ...newContract, client_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl"
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Type de contrat</Label>
                <select
                  value={newContract.contract_type}
                  onChange={(e) => setNewContract({ ...newContract, contract_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl"
                >
                  <option value="hosting">Hébergement uniquement</option>
                  <option value="maintenance">Maintenance uniquement</option>
                  <option value="both">Hébergement + Maintenance</option>
                  <option value="services">Services mensuels</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix mensuel (€ HT) *</Label>
                  <Input
                    type="number"
                    value={newContract.monthly_price}
                    onChange={(e) => setNewContract({ ...newContract, monthly_price: e.target.value })}
                    placeholder="49"
                    className="input-default"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heures incluses</Label>
                  <Input
                    type="number"
                    value={newContract.hours_included}
                    onChange={(e) => setNewContract({ ...newContract, hours_included: e.target.value })}
                    placeholder="2"
                    className="input-default"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Input
                    type="date"
                    value={newContract.start_date}
                    onChange={(e) => setNewContract({ ...newContract, start_date: e.target.value })}
                    className="input-default"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Engagement</Label>
                  <select
                    value={newContract.engagement}
                    onChange={(e) => setNewContract({ ...newContract, engagement: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl"
                  >
                    <option value="Sans engagement">Sans engagement</option>
                    <option value="6 mois">6 mois</option>
                    <option value="12 mois">12 mois</option>
                    <option value="24 mois">24 mois</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>URL du site</Label>
                <Input
                  value={newContract.website_url}
                  onChange={(e) => setNewContract({ ...newContract, website_url: e.target.value })}
                  placeholder="https://www.monsite.fr"
                  className="input-default"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white" 
                  style={{ backgroundColor: theme.colors?.accent }}
                  disabled={creating}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Créer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Task Modal */}
        <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
          <DialogContent aria-describedby="create-task-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouvelle tâche de maintenance</DialogTitle>
            </DialogHeader>
            <p id="create-task-desc" className="sr-only">Créer une tâche de maintenance</p>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label>Contrat *</Label>
                <select
                  value={newTask.contract_id}
                  onChange={(e) => setNewTask({ ...newTask, contract_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl"
                  required
                >
                  <option value="">Sélectionner un contrat</option>
                  {contracts.filter(c => c.contract_type !== 'hosting').map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Titre de la tâche *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Ex: Mise à jour WordPress"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Détails de l'intervention..."
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select
                  value={newTask.task_type}
                  onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl"
                >
                  <option value="initiative">Initiative agence</option>
                  <option value="client_request">Demande client</option>
                </select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white" 
                  style={{ backgroundColor: theme.colors?.accent }}
                  disabled={creating}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Créer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Team Modal */}
        <Dialog open={showAssignTeamModal} onOpenChange={setShowAssignTeamModal}>
          <DialogContent className="max-w-lg" aria-describedby="assign-team-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>
                Assigner l'équipe - {selectedContract?.name}
              </DialogTitle>
            </DialogHeader>
            <p id="assign-team-desc" className="sr-only">Assigner des membres à ce contrat</p>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Sélectionnez les membres responsables de ce contrat de maintenance.
              </p>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {teamMembers.map((member) => (
                  <div 
                    key={member.id} 
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                      selectedTeamMemberIds.includes(member.id) 
                        ? "border-cyan-500 bg-cyan-50" 
                        : "border-slate-200 hover:border-slate-300"
                    )}
                    onClick={() => toggleTeamMemberSelection(member.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-5 h-5 rounded border-2 flex items-center justify-center",
                        selectedTeamMemberIds.includes(member.id) 
                          ? "border-cyan-500 bg-cyan-500" 
                          : "border-slate-300"
                      )}>
                        {selectedTeamMemberIds.includes(member.id) && (
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white text-xs font-semibold">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {member.team_member_type === 'internal' ? 'Interne' : 
                           member.team_member_type === 'external' ? 'Externe' : 'Membre'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {teamMembers.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucun membre d'équipe disponible</p>
                  </div>
                )}
              </div>
              {selectedTeamMemberIds.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded-lg">
                  <Badge className="bg-cyan-500">{selectedTeamMemberIds.length}</Badge>
                  <span className="text-sm text-cyan-700">membre(s) sélectionné(s)</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAssignTeamModal(false);
                  setSelectedTeamMemberIds([]);
                  setSelectedContract(null);
                }}
                className="rounded-full"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleAssignTeamToContract}
                disabled={creating}
                className="rounded-full text-white"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ContractsPage;
